(() => {
  'use strict';

  const elementName = 'kiekkokingi-estimated-counter';
  if (customElements.get(elementName)) return;

  const DAY_IN_MILLISECONDS = 86400000;
  const DIGIT_TRANSITION_MILLISECONDS = 700;
  const DIGIT_TRANSITION_CLEANUP_MILLISECONDS = DIGIT_TRANSITION_MILLISECONDS + 60;
  const INTRO_WINDOW_MILLISECONDS = 30000;
  const INTRO_EARLIEST_EVENT_MILLISECONDS = 3500;
  const INTRO_LATEST_EVENT_MILLISECONDS = INTRO_WINDOW_MILLISECONDS - 2000;
  const INTRO_MINIMUM_GAP_MILLISECONDS = 4500;
  const INTRO_MINIMUM_GAP_DIFFERENCE_MILLISECONDS = 1500;

  class KiekkoKingiEstimatedCounter extends HTMLElement {
    connectedCallback() {
      if (this.isInitialized) return;
      this.isInitialized = true;
      const isReconnect = this.hasConnected;
      this.hasConnected = true;

      this.digits = this.querySelector('[data-counter-digits]');
      this.accessibleValue = this.querySelector('[data-counter-accessible]');
      if (!this.digits) return;

      this.baseValue = this.integerFromData('baseValue', 150000, 0);
      this.initialValue = this.integerFromData('initialValue', this.baseValue, 0);
      this.dailyGrowth = this.integerFromData('dailyGrowth', 720, 0);
      this.maximumValue = this.integerFromData('maxValue', 0, 0);
      this.introEnabled = this.booleanFromData('introEnabled', true);
      this.animateDigits = this.booleanFromData('animateDigits', true);
      const serverEpochFromMarkup = this.integerFromData(
        'serverNow',
        Math.floor(Date.now() / 1000),
        0,
      ) * 1000;
      if (!this.clockInitialized) {
        this.serverEpochAtConnect = serverEpochFromMarkup;
        this.performanceAtConnect = performance.now();
        this.clockInitialized = true;
      }
      this.startEpoch = this.integerFromData(
        'startEpoch',
        Math.floor(this.serverEpochAtConnect / 1000),
        0,
      ) * 1000;
      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

      const locale = this.dataset.locale || document.documentElement.lang || 'fi';
      try {
        this.formatter = new Intl.NumberFormat(locale, {
          maximumFractionDigits: 0,
          useGrouping: true,
        });
      } catch {
        this.formatter = new Intl.NumberFormat('fi-FI', {
          maximumFractionDigits: 0,
          useGrouping: true,
        });
      }

      const estimatedInitialValue = this.limitValue(
        Math.max(this.initialValue, this.estimatedValue(this.serverNow())),
      );
      this.currentValue = isReconnect && Number.isFinite(this.currentValue)
        ? this.limitValue(Math.max(this.currentValue, estimatedInitialValue))
        : estimatedInitialValue;
      this.renderValue(this.currentValue, false);

      this.onVisibilityChange = this.handleVisibilityChange.bind(this);
      document.addEventListener('visibilitychange', this.onVisibilityChange);

      if (isReconnect && this.hasStarted) {
        if (!Number.isFinite(this.steadyStartedAt)) {
          if (this.introPendingIncrements > 0) {
            this.schedulePendingIntroIncrement();
          } else if (
            this.introStageIndex < (this.introPlan?.length || 0)
            && !(this.maximumValue > 0 && this.currentValue >= this.maximumValue)
          ) {
            this.scheduleNextIntroStage();
          } else {
            this.startSteadyGrowth(this.currentValue);
          }
        } else {
          const currentEstimate = this.steadyValue();
          if (currentEstimate !== this.currentValue) {
            this.renderValue(currentEstimate, false);
          }
          this.scheduleNextGrowth();
        }
        return;
      }

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            if (
              !entries.some(
                (entry) => entry.isIntersecting && entry.intersectionRatio >= 0.35,
              )
            ) {
              return;
            }
            this.observer.disconnect();
            this.observer = undefined;
            this.beginIntro();
          },
          {
            rootMargin: '0px 0px -10% 0px',
            threshold: 0.35,
          },
        );
        this.observer.observe(this);
      } else {
        this.beginIntro();
      }
    }

    disconnectedCallback() {
      this.isInitialized = false;
      this.clearTimers();
      if (this.observer) this.observer.disconnect();
      if (this.onVisibilityChange) {
        document.removeEventListener('visibilitychange', this.onVisibilityChange);
      }
      this.observer = undefined;
      this.onVisibilityChange = undefined;
    }

    integerFromData(key, fallback, minimum, maximum = Number.MAX_SAFE_INTEGER) {
      const parsed = Number.parseInt(this.dataset[key], 10);
      if (!Number.isFinite(parsed)) return fallback;
      return Math.min(maximum, Math.max(minimum, parsed));
    }

    booleanFromData(key, fallback) {
      const value = this.dataset[key];
      if (value === undefined) return fallback;
      return value === 'true';
    }

    serverNow() {
      return this.serverEpochAtConnect + (performance.now() - this.performanceAtConnect);
    }

    limitValue(value) {
      const integer = Math.max(0, Math.floor(value));
      if (this.maximumValue > 0) return Math.min(integer, this.maximumValue);
      return integer;
    }

    estimatedValue(atTime) {
      const elapsed = Math.max(0, atTime - this.startEpoch);
      const growth = this.dailyGrowth > 0
        ? Math.floor((elapsed * this.dailyGrowth) / DAY_IN_MILLISECONDS)
        : 0;
      return this.limitValue(this.baseValue + growth);
    }

    randomInteger(minimum, maximum) {
      return Math.floor(Math.random() * (maximum - minimum + 1)) + minimum;
    }

    createRandomIntroPlan() {
      let eventTimes;

      for (let attempt = 0; attempt < 120; attempt += 1) {
        const candidateTimes = [
          this.randomInteger(
            INTRO_EARLIEST_EVENT_MILLISECONDS,
            INTRO_LATEST_EVENT_MILLISECONDS,
          ),
          this.randomInteger(
            INTRO_EARLIEST_EVENT_MILLISECONDS,
            INTRO_LATEST_EVENT_MILLISECONDS,
          ),
          this.randomInteger(
            INTRO_EARLIEST_EVENT_MILLISECONDS,
            INTRO_LATEST_EVENT_MILLISECONDS,
          ),
        ].sort((first, second) => first - second);
        const firstGap = candidateTimes[1] - candidateTimes[0];
        const secondGap = candidateTimes[2] - candidateTimes[1];

        if (
          firstGap >= INTRO_MINIMUM_GAP_MILLISECONDS
          && secondGap >= INTRO_MINIMUM_GAP_MILLISECONDS
          && Math.abs(firstGap - secondGap)
            >= INTRO_MINIMUM_GAP_DIFFERENCE_MILLISECONDS
        ) {
          eventTimes = candidateTimes;
          break;
        }
      }

      if (!eventTimes) {
        // The non-overlapping ranges guarantee separated, uneven fallback timings.
        eventTimes = [
          this.randomInteger(3500, 5500),
          this.randomInteger(11000, 13000),
          this.randomInteger(25000, INTRO_LATEST_EVENT_MILLISECONDS),
        ];
      }

      const doubleIncrementIndex = this.randomInteger(0, 2);
      return eventTimes.map((at, index) => ({
        at,
        increment: index === doubleIncrementIndex ? 2 : 1,
      }));
    }

    beginIntro() {
      if (this.hasStarted) return;
      this.hasStarted = true;

      const visibleValue = this.estimatedValue(this.serverNow());
      this.currentValue = visibleValue;
      this.introPlan = this.introEnabled ? this.createRandomIntroPlan() : [];
      this.introStartedAt = this.serverNow();
      this.introStageIndex = 0;
      this.introPendingIncrements = 0;
      this.renderValue(visibleValue, false);

      if (
        this.introPlan.length === 0
        || (this.maximumValue > 0 && this.currentValue >= this.maximumValue)
      ) {
        this.startSteadyGrowth(this.currentValue);
        return;
      }

      this.scheduleNextIntroStage();
    }

    scheduleNextIntroStage() {
      this.clearIntroTimer();

      if (
        !this.hasStarted
        || !this.isConnected
        || this.introStageIndex >= this.introPlan.length
        || (this.maximumValue > 0 && this.currentValue >= this.maximumValue)
      ) {
        if (this.hasStarted && this.isConnected) {
          this.startSteadyGrowth(this.currentValue);
        }
        return;
      }

      const stageIndex = this.introStageIndex;
      const event = this.introPlan[stageIndex];
      const elapsed = Math.max(0, this.serverNow() - this.introStartedAt);
      const delay = Math.max(0, event.at - elapsed);
      const increment = Math.max(1, event.increment || 1);

      this.introTimer = window.setTimeout(() => {
        this.introTimer = undefined;
        if (!this.isConnected) return;

        this.introPendingIncrements = increment;
        this.introStageIndex += 1;
        this.runNextIntroIncrement();
      }, delay);
    }

    runNextIntroIncrement() {
      if (!this.hasStarted || !this.isConnected) return;

      if (
        this.introPendingIncrements <= 0
        || (this.maximumValue > 0 && this.currentValue >= this.maximumValue)
      ) {
        this.introPendingIncrements = 0;
        this.finishIntroStage();
        return;
      }

      const nextValue = this.limitValue(this.currentValue + 1);
      this.introPendingIncrements -= 1;

      if (nextValue !== this.currentValue) {
        this.renderValue(nextValue, true);
      }

      if (
        this.introPendingIncrements > 0
        && !(this.maximumValue > 0 && nextValue >= this.maximumValue)
      ) {
        this.schedulePendingIntroIncrement();
        return;
      }

      this.introPendingIncrements = 0;
      this.finishIntroStage();
    }

    schedulePendingIntroIncrement() {
      this.clearIntroTimer();
      if (!this.hasStarted || !this.isConnected || this.introPendingIncrements <= 0) {
        return;
      }

      this.introTimer = window.setTimeout(() => {
        this.introTimer = undefined;
        this.runNextIntroIncrement();
      }, DIGIT_TRANSITION_CLEANUP_MILLISECONDS);
    }

    finishIntroStage() {
      if (
        this.introStageIndex >= this.introPlan.length
        || (this.maximumValue > 0 && this.currentValue >= this.maximumValue)
      ) {
        this.startSteadyGrowth(this.currentValue);
        return;
      }

      this.scheduleNextIntroStage();
    }

    startSteadyGrowth(value, startedAt = this.serverNow()) {
      this.clearIntroTimer();
      this.introStageIndex = this.introPlan?.length || 0;
      this.introPendingIncrements = 0;
      this.steadyStartValue = this.limitValue(value);
      this.steadyStartedAt = startedAt;
      this.scheduleNextGrowth();
    }

    steadyValue() {
      if (
        !Number.isFinite(this.steadyStartedAt)
        || !Number.isFinite(this.steadyStartValue)
        || this.dailyGrowth <= 0
      ) {
        return this.currentValue;
      }

      const interval = DAY_IN_MILLISECONDS / this.dailyGrowth;
      const elapsed = Math.max(0, this.serverNow() - this.steadyStartedAt);
      return this.limitValue(this.steadyStartValue + Math.floor(elapsed / interval));
    }

    scheduleNextGrowth() {
      this.clearGrowthTimer();
      if (!this.hasStarted || document.hidden || this.dailyGrowth <= 0) return;
      if (this.maximumValue > 0 && this.currentValue >= this.maximumValue) return;

      if (!Number.isFinite(this.steadyStartedAt)) return;

      const now = this.serverNow();
      const interval = DAY_IN_MILLISECONDS / this.dailyGrowth;
      const elapsed = Math.max(0, now - this.steadyStartedAt);
      const currentGrowth = Math.floor(elapsed / interval);
      const nextGrowthAt = this.steadyStartedAt + ((currentGrowth + 1) * interval);
      const delay = Math.max(100, nextGrowthAt - now + 25);

      this.growthTimer = window.setTimeout(() => {
        if (!this.isConnected) return;
        const nextValue = this.steadyValue();
        if (nextValue !== this.currentValue) this.renderValue(nextValue, true);
        this.scheduleNextGrowth();
      }, delay);
    }

    renderValue(value, animate) {
      const nextValue = this.limitValue(value);
      const nextFormatted = this.formatter.format(nextValue);
      const shouldAnimate = Boolean(
        animate
        && this.animateDigits
        && !this.reducedMotion.matches
        && this.currentFormatted
        && this.currentFormatted !== nextFormatted,
      );

      this.clearRenderTimer();
      this.digits.classList.remove('is-animating');

      if (shouldAnimate) {
        this.renderTransition(this.currentFormatted, nextFormatted);
      } else {
        this.renderStatic(nextFormatted);
      }

      this.currentValue = nextValue;
      this.currentFormatted = nextFormatted;
      if (this.accessibleValue) this.accessibleValue.textContent = nextFormatted;
    }

    renderStatic(formattedValue) {
      const fragment = document.createDocumentFragment();

      Array.from(formattedValue).forEach((character) => {
        if (/\d/.test(character)) {
          const glyph = document.createElement('span');
          glyph.className = 'kk-home-used-discs__counter-glyph';
          glyph.textContent = character;
          fragment.append(glyph);
          return;
        }

        const separator = document.createElement('span');
        separator.className = 'kk-home-used-discs__counter-separator';
        separator.textContent = character;
        fragment.append(separator);
      });

      this.digits.replaceChildren(fragment);
    }

    renderTransition(previousFormatted, nextFormatted) {
      const previousCharacters = Array.from(previousFormatted);
      const nextCharacters = Array.from(nextFormatted);
      const missingCharacters = Math.max(0, nextCharacters.length - previousCharacters.length);
      const alignedPrevious = Array(missingCharacters).fill('').concat(previousCharacters);
      const fragment = document.createDocumentFragment();

      nextCharacters.forEach((nextCharacter, index) => {
        const previousCharacter = alignedPrevious[index] || '';
        if (!/\d/.test(nextCharacter)) {
          const separator = document.createElement('span');
          separator.className = 'kk-home-used-discs__counter-separator';
          separator.textContent = nextCharacter;
          fragment.append(separator);
          return;
        }

        const glyph = document.createElement('span');
        glyph.className = 'kk-home-used-discs__counter-glyph';

        if (previousCharacter !== nextCharacter && /\d/.test(previousCharacter)) {
          glyph.classList.add('is-changing');

          const outgoing = document.createElement('span');
          outgoing.className = 'kk-home-used-discs__counter-outgoing';
          outgoing.textContent = previousCharacter;

          const incoming = document.createElement('span');
          incoming.className = 'kk-home-used-discs__counter-incoming';
          incoming.textContent = nextCharacter;

          glyph.append(outgoing, incoming);
        } else {
          glyph.textContent = nextCharacter;
        }

        fragment.append(glyph);
      });

      this.digits.replaceChildren(fragment);
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (this.isConnected) this.digits.classList.add('is-animating');
        });
      });

      this.renderTimer = window.setTimeout(() => {
        if (!this.isConnected || this.currentFormatted !== nextFormatted) return;
        this.digits.classList.remove('is-animating');
        this.renderStatic(nextFormatted);
      }, DIGIT_TRANSITION_CLEANUP_MILLISECONDS);
    }

    handleVisibilityChange() {
      if (!this.hasStarted) return;
      if (document.hidden) {
        this.clearGrowthTimer();
        return;
      }

      const currentEstimate = this.steadyValue();
      if (currentEstimate !== this.currentValue) this.renderValue(currentEstimate, true);
      this.scheduleNextGrowth();
    }

    clearGrowthTimer() {
      if (this.growthTimer) window.clearTimeout(this.growthTimer);
      this.growthTimer = undefined;
    }

    clearRenderTimer() {
      if (this.renderTimer) window.clearTimeout(this.renderTimer);
      this.renderTimer = undefined;
    }

    clearIntroTimer() {
      if (this.introTimer) window.clearTimeout(this.introTimer);
      this.introTimer = undefined;
    }

    clearTimers() {
      this.clearGrowthTimer();
      this.clearRenderTimer();
      this.clearIntroTimer();
    }

  }

  customElements.define(elementName, KiekkoKingiEstimatedCounter);
})();
